import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress, {
  linearProgressClasses
} from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material";
import styled, { keyframes } from "styled-components";

const appear = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

export const Tooltip = styled(Paper)`
  position: relative;
  width: 450px;
  max-width: calc(100vw - ${({ theme }) => theme.spacing(4)});
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: 12px;
  /* A step with a lot of text scrolls inside the tooltip instead of being
     cut off by the window edge. */
  max-height: calc(100vh - ${({ theme }) => theme.spacing(4)});
  overflow-x: hidden;
  overflow-y: auto;
  animation: ${appear} 0.18s ease-out;

  /* Accent bar tying the tooltip to the app's palette. */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.palette.primary.main},
      ${({ theme }) => theme.palette.secondary.main}
    );
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding-right: ${({ theme }) => theme.spacing(4)};
`;

export const IconBadge = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: ${({ theme }) => theme.palette.primary.main};
  background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.12)};
`;

export const Title = styled(Typography)`
  font-weight: 600;
`;

export const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1.5)};
  right: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const Content = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  /* Lets a step's plain-string content break lines with \n, while long lines
     still wrap. */
  white-space: pre-wrap;
`;

export const Tip = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: 8px;
  color: ${({ theme }) => theme.palette.text.primary};
  background-color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.07)
      : alpha(theme.palette.primary.main, 0.06)};
`;

export const TipIcon = styled.div`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const Progress = styled(LinearProgress)`
  /* Bleeds over the tooltip's padding, sitting right on top of the footer. */
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-right: -${({ theme }) => theme.spacing(3)};
  margin-left: -${({ theme }) => theme.spacing(3)};
  height: 4px;
  border-radius: 30px;

  &.${linearProgressClasses.colorPrimary} {
    background-color: ${({ theme }) =>
      theme.palette.mode === "dark"
        ? theme.palette.grey[900]
        : theme.palette.grey[100]};
  }

  & .${linearProgressClasses.bar} {
    border-radius: 30px;
    background-color: ${({ theme }) => theme.palette.secondary.main};
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  /* Bleeds over the tooltip's padding so the background spans the full card. */
  margin-right: -${({ theme }) => theme.spacing(3)};
  margin-bottom: -${({ theme }) => theme.spacing(3)};
  margin-left: -${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(1.5, 3)};
  background-color: ${({ theme }) =>
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.04)};
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

// Stands in for the Next button on steps the user has to advance themselves.
export const ClickHint = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding-left: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const PrimaryButton = styled(Button)`
  border-radius: 20px;
  padding-left: ${({ theme }) => theme.spacing(2.5)};
  padding-right: ${({ theme }) => theme.spacing(2.5)};
`;
